import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { notificationApi, presenceApi } from "../../../services/api";
import { useToast } from "../Toast/ToastProvider";
import "./Notifications.css";

const NotificationContext = createContext(null);

const hasToken = () =>
  !!(localStorage.getItem("accessToken") || localStorage.getItem("cftoken"));

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const load = useCallback(async () => {
    if (!hasToken()) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const res = await notificationApi.list();
      setNotifications(res.data || []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!hasToken()) return undefined;

    const ping = () => presenceApi.ping().catch(() => {});
    ping();
    const presenceId = setInterval(ping, 2 * 60 * 1000);
    const pollId = setInterval(load, 45 * 1000);

    return () => {
      clearInterval(presenceId);
      clearInterval(pollId);
    };
  }, [load]);

  const markRead = async (id) => {
    try {
      await notificationApi.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n))
      );
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  const pushLocal = useCallback(
    ({ title, message, type = "info" }) => {
      showToast({ title, message, type });
    },
    [showToast]
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        open,
        setOpen,
        loading,
        load,
        markRead,
        markAllRead,
        pushLocal,
      }}
    >
      {children}
      {open && (
        <div
          className="notif-drawer-backdrop"
          onClick={() => setOpen(false)}
          role="presentation"
        />
      )}
      {open && (
        <aside className="notif-drawer" aria-label="Notifications">
          <div className="notif-drawer-head">
            <h3>Notifications</h3>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close">
              ✕
            </button>
          </div>
          {unreadCount > 0 && (
            <button type="button" className="notif-mark-all" onClick={markAllRead}>
              Mark all read
            </button>
          )}
          <div className="notif-list">
            {loading && <p className="notif-empty">Loading…</p>}
            {!loading && notifications.length === 0 && (
              <p className="notif-empty">No notifications yet.</p>
            )}
            {notifications.map((n) => (
              <button
                key={n._id}
                type="button"
                className={`notif-item ${n.read ? "read" : ""} type-${n.type}`}
                onClick={() => markRead(n._id)}
              >
                <strong>{n.title}</strong>
                <span>{n.message}</span>
                <small>{new Date(n.createdAt).toLocaleString()}</small>
              </button>
            ))}
          </div>
        </aside>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}
